import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';
import toastError from "../../errors/toastError";
import { getTokens } from '../../styles/tokens';
import useDialogStyles from '../TransferTicketModalCustom/dialogStyles';

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    timeline: {
      listStyle: "none",
      margin: 0,
      padding: "2px 0 8px",
    },
    item: {
      position: "relative",
      display: "flex",
      gap: 12,
      paddingBottom: 16,
      "&:last-child": { paddingBottom: 0 },
      // linha que liga um ponto ao seguinte
      "&:not(:last-child)::before": {
        content: '""',
        position: "absolute",
        left: 5,
        top: 16,
        bottom: -2,
        width: 2,
        borderRadius: 1,
        backgroundColor: t.line,
      },
    },
    dot: {
      flex: "none",
      width: 12,
      height: 12,
      marginTop: 4,
      borderRadius: "50%",
      backgroundColor: t.accent,
      boxShadow: `0 0 0 3px ${t.accentTint.medium}`,
    },
    body: {
      minWidth: 0,
      flex: 1,
    },
    text: {
      fontSize: 13.5,
      lineHeight: "19px",
      color: t.text,
      overflowWrap: "anywhere",
    },
    time: {
      marginTop: 1,
      fontSize: 12,
      lineHeight: "16px",
      color: t.textSecondary,
      fontVariantNumeric: "tabular-nums",
    },
  };
});

const ShowTicketLogModal = ({ isOpen, handleClose, ticketId }) => {
  const dialogClasses = useDialogStyles({ width: 460 });
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const typeDescriptions = {
    create: i18n.t("showTicketLogModal.options.create"),
    chatBot: i18n.t("showTicketLogModal.options.chatBot"),
    queue: i18n.t("showTicketLogModal.options.queue"),
    open: i18n.t("showTicketLogModal.options.open"),
    access: i18n.t("showTicketLogModal.options.access"),
    transfered: i18n.t("showTicketLogModal.options.transfered"),
    receivedTransfer: i18n.t("showTicketLogModal.options.receivedTransfer"),
    pending: i18n.t("showTicketLogModal.options.pending"),
    closed: i18n.t("showTicketLogModal.options.closed"),
    reopen: i18n.t("showTicketLogModal.options.reopen"),
    redirect: i18n.t("showTicketLogModal.options.redirect"),
    clientClosed: i18n.t("showTicketLogModal.options.clientClosed")
    // Adicione outros mapeamentos conforme necessário
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchLogs = async () => {
        try {
          const { data } = await api.get(`/tickets-log/${ticketId}`);
          setLogs(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchLogs();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId]);

  // quem fez (atendente, fila ou os dois), conforme o tipo do registro
  const logActor = (log) => {
    if (
      log.type === 'access' ||
      log.type === 'transfered' ||
      log.type === 'open' || log.type === 'pending' || log.type === "closed" || log.type === "reopen"
    ) {
      return log?.user?.name;
    }
    if (log?.type === 'queue' || log?.type === 'redirect') {
      return log?.queue?.name;
    }
    if (log.type === 'receivedTransfer') {
      return log?.queue?.name + ' - ' + log?.user?.name;
    }
    return '';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="ticket-log-title"
    >
      <div className={dialogClasses.header}>
        <h2 id="ticket-log-title" className={dialogClasses.title}>
          {i18n.t('showTicketLogModal.title.header')}
        </h2>
        {/* TODO i18n */}
        <p className={dialogClasses.subtitle}>
          Tudo o que aconteceu neste atendimento, em ordem.
        </p>
      </div>
      <DialogContent className={dialogClasses.content}>
        {loading && logs.length === 0 ? (
          <div className={dialogClasses.loading}>
            <CircularProgress size={18} color="inherit" />
          </div>
        ) : logs.length === 0 ? (
          // TODO i18n
          <div className={dialogClasses.empty}>Nenhum registro neste atendimento.</div>
        ) : (
          <ol className={classes.timeline}>
            {logs.map((log, index) => (
              <li key={index} className={classes.item}>
                <span className={classes.dot} aria-hidden="true" />
                <div className={classes.body}>
                  <div className={classes.text}>
                    {`${logActor(log) || ''} ${typeDescriptions[log.type] || log.type || ''}`.trim()}
                  </div>
                  <div className={classes.time}>
                    {format(parseISO(log?.createdAt), 'dd/MM/yyyy HH:mm')}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        <Button
          onClick={handleClose}
          color="primary"
          variant="contained"
          className={dialogClasses.confirm}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShowTicketLogModal;
